/* eslint-disable react/prop-types */
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMaterialUIController } from "context";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Divider from "@mui/material/Divider";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import useApi from "components/Utils/api";
import toast from "utils/toast";

const EMPTY_ITEM = () => ({ itemName: "", rate: "", quantity: "" });
const noop = () => {};

function SectionHeader({ label, mt = 2.5 }) {
  return (
    <MDBox mt={mt} mb={0.5}>
      <MDTypography
        variant="caption"
        fontWeight="bold"
        color="info"
        sx={{ textTransform: "uppercase", letterSpacing: "0.06em", fontSize: "0.72rem" }}
      >
        {label}
      </MDTypography>
      <Divider sx={{ mt: 0.5, mb: 1.5 }} />
    </MDBox>
  );
}

function parseDMY(str) {
  if (!str) return "";
  const parts = str.split("/");
  if (parts.length === 3) return `${parts[2]}-${parts[1]}-${parts[0]}`;
  return str.split("T")[0];
}

function buildFormFromInvoice(invoice) {
  return {
    billingCompany: invoice.billingCompany || "",
    billingAddress: invoice.billingAddress || "",
    sameAsBilling: invoice.billingAddress === invoice.shippingAddress,
    shippingAddress: invoice.shippingAddress || "",
    currency: invoice.currency || "AED",
    items: invoice.items?.length
      ? invoice.items.map((it) => ({
          id: it.id,
          itemName: it.itemName,
          rate: String(it.rate),
          quantity: String(it.quantity),
        }))
      : [EMPTY_ITEM()],
    isScheduled: !!invoice.isScheduled,
    scheduleDate: invoice.isScheduled && invoice.issueDate ? parseDMY(invoice.issueDate) : "",
    scheduleTime: "00:00",
    hasExpiry: !!invoice.hasExpiry,
    expiryDate: invoice.hasExpiry && invoice.expiryDate ? parseDMY(invoice.expiryDate) : "",
    expiryTime: "00:00",
    notifyEmail: !!invoice.notifyViaEmail,
    notifyEmailAddress: invoice.notifiedEmail || "",
    notifySms: !!invoice.notifyViaSms,
    notifyPhoneNumber: invoice.notifiedNumber || "",
    remarks: invoice.remarks || "",
    conditions: invoice.conditions || "",
    createPaymentLink:
      invoice.hasPaymentLink === "1" || invoice.hasPaymentLink === 1 || invoice.hasPaymentLink === true,
    vatEnabled: invoice.hasTax === 1 || invoice.hasTax === true,
    taxPercent: invoice.taxPercent ? String(invoice.taxPercent) : "",
  };
}

function EditInvoice() {
  const location = useLocation();
  const navigate = useNavigate();
  const api = useApi();
  const [controller] = useMaterialUIController();
  const { merchant, sidenavColor } = controller;

  const editInvoice = location.state?.invoice ?? null;

  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [form, setForm] = useState(() => (editInvoice ? buildFormFromInvoice(editInvoice) : null));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (!editInvoice) {
      navigate("/invoices");
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    api.get("customers")
      .then((res) => {
        const data = res.data?.data ?? [];
        const activeCustomers = data.filter(
          (c) => c.status === "ACTIVE" || c.status === true || c.status === 1 || c.status === "1"
        );
        setCustomers(activeCustomers);
        if (editInvoice) {
          const matched = activeCustomers.find((c) => c.customerId === editInvoice.customerId) ?? null;
          setSelectedCustomer(matched);
        }
      })
      .catch(() => {});
  }, []); // eslint-disable-line

  if (!editInvoice || !form) {
    return null;
  }

  const set = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    setErrors((prev) => ({ ...prev, [field]: false }));
  };

  const getItemTotal = (it) => parseFloat(it.rate || 0) * parseFloat(it.quantity || 0);
  const grandTotal = form.items.reduce((s, it) => s + getItemTotal(it), 0);
  const taxAmount = form.vatEnabled ? (grandTotal * parseFloat(form.taxPercent || 0)) / 100 : 0;

  const handleUpdate = () => {
    if (!form.billingCompany.trim()) {
      setErrors({ billingCompany: true });
      return;
    }
    const now = new Date();
    const body = {
      ...editInvoice,
      billingCompany: form.billingCompany,
      remarks: form.remarks,
      conditions: form.conditions,
      updatedBy: merchant?.mid,
      updatedDate: now.toISOString().split("T")[0],
    };
    setSaving(true);
    api.put(`invoices/${editInvoice.id}`, body)
      .then(() => {
        toast.success("Invoice updated.");
        navigate("/invoices");
      })
      .catch((error) => {
        if (error.response?.status === 403) {
          toast.error("You do not have permission to perform this action.");
        } else {
          toast.error("Failed to update invoice.");
        }
      })
      .finally(() => setSaving(false));
  };

  const handleDelete = () => {
    setSaving(true);
    api.delete(`invoices/${editInvoice.id}`)
      .then(() => {
        toast.success("Invoice deleted.");
        navigate("/invoices");
      })
      .catch((error) => {
        if (error.response?.status === 403) {
          toast.error("You do not have permission to perform this action.");
        } else {
          toast.error("Failed to delete invoice.");
        }
      })
      .finally(() => {
        setSaving(false);
        setConfirmDelete(false);
      });
  };

  const inputSx = { mb: 1.5 };
  const labelSx = { "& .MuiFormControlLabel-label": { fontSize: "0.875rem" } };

  return (
    <DashboardLayout>
      <DashboardNavbar breadcrumbTitle="Edit Invoice" />
      <MDBox mt={3} mb={3}>
        <Grid container rowSpacing={0} columnSpacing={7} justifyContent="flex-start">
          <Grid item xs={12}>
            <MDBox mb={3} display="flex" alignItems="center">
              <MDBox flex={1}>
                <MDTypography variant="h5" fontWeight="bold" sx={{ lineHeight: 1.2, mb: 0.25 }}>
                  Edit Invoice
                </MDTypography>
                <MDTypography variant="button" color="text" fontWeight="regular" sx={{ lineHeight: 1 }}>
                  {editInvoice.invoiceNumber}
                </MDTypography>
              </MDBox>
              <MDBox display="flex" gap={1}>
                <MDButton
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={() => setConfirmDelete(true)}
                  disabled={saving}
                >
                  <Icon sx={{ mr: 0.5 }} fontSize="small">delete</Icon>
                  Delete
                </MDButton>
                <MDButton variant="gradient" color={sidenavColor} size="small" onClick={handleUpdate} disabled={saving}>
                  <Icon sx={{ mr: 0.5 }} fontSize="small">save</Icon>
                  {saving ? "Updating…" : "Update"}
                </MDButton>
              </MDBox>
            </MDBox>
          </Grid>

          <Grid item xs={12} lg={6}>
            <MDBox>
              <MDBox mb={0.5}>
                <Autocomplete
                  size="small"
                  options={customers || []}
                  getOptionLabel={(c) => `${c.firstName} ${c.lastName}`}
                  value={selectedCustomer}
                  disabled
                  onChange={noop}
                  renderInput={(params) => (
                    <TextField {...params} label="Select Customer (optional — prefills fields below)" />
                  )}
                />
              </MDBox>

              <SectionHeader label="Billing Info" />

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Billing To"
                    fullWidth
                    size="small"
                    required
                    value={form.billingCompany}
                    onChange={set("billingCompany")}
                    error={!!errors.billingCompany}
                    helperText={errors.billingCompany ? "Billing Company is required" : ""}
                    sx={inputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Currency"
                    fullWidth
                    size="small"
                    value={form.currency}
                    disabled
                    onChange={noop}
                    sx={inputSx}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2} alignItems="flex-start">
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Billing Address"
                    fullWidth
                    size="small"
                    multiline
                    rows={3}
                    value={form.billingAddress}
                    disabled
                    onChange={noop}
                    sx={inputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <MDBox>
                    <TextField
                      label="Shipping Address"
                      fullWidth
                      size="small"
                      multiline
                      rows={3}
                      value={form.sameAsBilling ? form.billingAddress : form.shippingAddress}
                      disabled
                      onChange={noop}
                      sx={{ mb: 0.5 }}
                    />
                    <MDBox display="flex" justifyContent="flex-end">
                      <FormControlLabel
                        sx={{
                          m: 0,
                          "& .MuiFormControlLabel-label": {
                            fontSize: "0.75rem",
                            fontWeight: 400,
                            color: "text.secondary",
                          },
                        }}
                        control={<Checkbox size="small" checked={form.sameAsBilling} disabled onChange={noop} sx={{ p: 0.25, color: "text.secondary" }} />}
                        label="Same as billing"
                      />
                    </MDBox>
                  </MDBox>
                </Grid>
              </Grid>

              <SectionHeader label="Items" />

              <Grid container spacing={1} sx={{ mb: 0.5, px: 0.5 }}>
                <Grid item xs={4.5}>
                  <MDTypography variant="caption" fontWeight="bold" color="text">Item Name</MDTypography>
                </Grid>
                <Grid item xs={2.5}>
                  <MDTypography variant="caption" fontWeight="bold" color="text">Rate</MDTypography>
                </Grid>
                <Grid item xs={2}>
                  <MDTypography variant="caption" fontWeight="bold" color="text">Qty</MDTypography>
                </Grid>
                <Grid item xs={2.5}>
                  <MDTypography variant="caption" fontWeight="bold" color="text">Total</MDTypography>
                </Grid>
                <Grid item xs={0.5} />
              </Grid>

              {form.items.map((item, idx) => {
                const itTotal = getItemTotal(item);
                return (
                  <Grid container spacing={1} alignItems="center" key={idx} sx={{ mb: 0.5 }}>
                    <Grid item xs={4.5}>
                      <TextField placeholder="Item name" fullWidth size="small" value={item.itemName} disabled onChange={noop} />
                    </Grid>
                    <Grid item xs={2.5}>
                      <TextField placeholder="0.00" fullWidth size="small" value={item.rate} disabled onChange={noop} />
                    </Grid>
                    <Grid item xs={2}>
                      <TextField placeholder="1" fullWidth size="small" value={item.quantity} disabled onChange={noop} />
                    </Grid>
                    <Grid item xs={2.5}>
                      <TextField
                        value={itTotal > 0 ? itTotal.toFixed(2) : "0.00"}
                        fullWidth
                        size="small"
                        InputProps={{ readOnly: true }}
                        sx={{ "& .MuiInputBase-input": { color: "rgba(0,0,0,0.45)", cursor: "default" } }}
                      />
                    </Grid>
                    <Grid item xs={0.5} sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <IconButton size="small" color="error" disabled sx={{ p: "4px" }}>
                        <DeleteOutlineIcon sx={{ fontSize: "1.1rem" }} />
                      </IconButton>
                    </Grid>
                  </Grid>
                );
              })}

              <MDBox display="flex" alignItems="center" justifyContent="flex-end" mt={1.5} mb={0.5}>
                <MDBox
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 2,
                    py: 0.6,
                    borderRadius: "6px",
                    backgroundColor: "rgba(26,115,232,0.07)",
                    border: "1px solid rgba(26,115,232,0.2)",
                  }}
                >
                  <MDTypography variant="button" fontWeight="bold" sx={{ textTransform: "none", fontSize: "0.82rem" }}>
                    Grand Total:
                  </MDTypography>
                  <MDTypography variant="button" color="info" fontWeight="bold" sx={{ textTransform: "none", fontSize: "0.9rem" }}>
                    {grandTotal.toFixed(2)}
                  </MDTypography>
                  {form.vatEnabled && taxAmount > 0 && (
                    <MDTypography variant="caption" color="text" sx={{ ml: 0.5 }}>
                      + {taxAmount.toFixed(2)} tax
                    </MDTypography>
                  )}
                </MDBox>
              </MDBox>

              <SectionHeader label="Remarks and Terms & Conditions" mt={4} />
              <MDBox display="flex" gap={2}>
                <MDBox sx={{ flex: 1 }}>
                  <TextField
                    label="Remarks"
                    size="small"
                    fullWidth
                    multiline
                    rows={3}
                    value={form.remarks}
                    onChange={set("remarks")}
                  />
                  <MDTypography variant="caption" color="text" sx={{ display: "block", mt: 0.5 }}>
                    Only visible to you
                  </MDTypography>
                </MDBox>
                <MDBox sx={{ flex: 1 }}>
                  <TextField
                    label="Terms & Conditions"
                    size="small"
                    fullWidth
                    multiline
                    rows={3}
                    value={form.conditions}
                    onChange={set("conditions")}
                  />
                  <MDTypography variant="caption" color="text" sx={{ display: "block", mt: 0.5 }}>
                    Visible at the end of invoice
                  </MDTypography>
                </MDBox>
              </MDBox>
            </MDBox>
          </Grid>

          <Grid item xs={12} lg={6}>
            <MDBox
              sx={{
                borderLeft: { xs: "none", lg: "1px solid #e0e0e0" },
                pl: { xs: 0, lg: 5 },
              }}
            >
              <SectionHeader label="Options" mt={-1} />

              <MDBox display="flex" gap={2} alignItems="flex-start">
                <MDBox flex={1}>
                  <FormControlLabel
                    sx={labelSx}
                    control={<Checkbox size="small" checked={form.isScheduled} disabled onChange={noop} />}
                    label="Schedule Invoice"
                  />
                  <Collapse in={form.isScheduled} timeout={300} unmountOnExit>
                    <MDBox sx={{ display: "flex", gap: 1, mt: 0.5 }}>
                      <TextField
                        label="Schedule Date"
                        type="date"
                        size="small"
                        value={form.scheduleDate}
                        disabled
                        onChange={noop}
                        InputLabelProps={{ shrink: true }}
                        sx={{ flex: 1 }}
                      />
                      <TextField
                        label="Schedule Time"
                        type="time"
                        size="small"
                        value={form.scheduleTime}
                        disabled
                        onChange={noop}
                        InputLabelProps={{ shrink: true }}
                        sx={{ flex: 1 }}
                      />
                    </MDBox>
                  </Collapse>
                </MDBox>

                <MDBox flex={1}>
                  <FormControlLabel
                    sx={labelSx}
                    control={<Checkbox size="small" checked={form.hasExpiry} disabled onChange={noop} />}
                    label="Expiry"
                  />
                  <Collapse in={form.hasExpiry} timeout={300} unmountOnExit>
                    <MDBox sx={{ display: "flex", gap: 1, mt: 0.5 }}>
                      <TextField
                        label="Expiry Date"
                        type="date"
                        size="small"
                        value={form.expiryDate}
                        disabled
                        onChange={noop}
                        InputLabelProps={{ shrink: true }}
                        sx={{ flex: 1 }}
                      />
                      <TextField
                        label="Expiry Time"
                        type="time"
                        size="small"
                        value={form.expiryTime}
                        disabled
                        onChange={noop}
                        InputLabelProps={{ shrink: true }}
                        sx={{ flex: 1 }}
                      />
                    </MDBox>
                  </Collapse>
                </MDBox>
              </MDBox>

              <MDBox display="flex" gap={2} alignItems="flex-start">
                <MDBox flex={1}>
                  <FormControlLabel
                    sx={labelSx}
                    control={<Checkbox size="small" checked={form.notifyEmail} disabled onChange={noop} />}
                    label="Notify via Email"
                  />
                  <Collapse in={form.notifyEmail} timeout={300} unmountOnExit>
                    <TextField
                      label="Email Address"
                      fullWidth
                      size="small"
                      value={form.notifyEmailAddress}
                      disabled
                      onChange={noop}
                      sx={{ mt: 0.5 }}
                    />
                  </Collapse>
                </MDBox>
              </MDBox>

              <SectionHeader label="Settings" />

              <MDBox display="flex" gap={2} alignItems="flex-start">
                <MDBox flex={1}>
                  <FormControlLabel
                    sx={labelSx}
                    control={<Checkbox size="small" checked={form.createPaymentLink} disabled onChange={noop} />}
                    label="Create Payment Link"
                  />
                </MDBox>
                <MDBox flex={1}>
                  <FormControlLabel
                    sx={labelSx}
                    control={<Checkbox size="small" checked={form.vatEnabled} disabled onChange={noop} />}
                    label="VAT"
                  />
                  {form.vatEnabled && (
                    <TextField
                      label="Tax %"
                      size="small"
                      fullWidth
                      value={form.taxPercent}
                      disabled
                      onChange={noop}
                      sx={{ mt: 0.5 }}
                    />
                  )}
                </MDBox>
              </MDBox>
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <MDTypography variant="h6" fontWeight="medium">Delete Invoice</MDTypography>
        </DialogTitle>
        <DialogContent>
          <MDTypography variant="body2" color="text">
            Are you sure you want to delete invoice <strong>{editInvoice.invoiceNumber}</strong>? This cannot be undone.
          </MDTypography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <MDButton variant="outlined" color="secondary" onClick={() => setConfirmDelete(false)} disabled={saving}>
            Cancel
          </MDButton>
          <MDButton variant="gradient" color="error" onClick={handleDelete} disabled={saving}>
            {saving ? "Deleting…" : "Delete"}
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default EditInvoice;
